import {
    Badge,
    Box,
    Button,
    Card,
    Code,
    Container,
    Flex,
    Grid,
    Heading,
    HStack,
    Icon,
    Input,
    SimpleGrid,
    Spinner,
    Stack,
    Table,
    Text,
} from '@chakra-ui/react';
import { ChangeEvent, DragEvent, useRef, useState } from 'react';
import { LuFileCheck2, LuFileUp, LuFlaskConical, LuRefreshCw, LuShieldAlert } from 'react-icons/lu';
import ErrorMessage from 'src/components/ErrorMessage';
import { useI18n } from 'src/i18n/I18nContext';
import {
    invoiceExtractionService,
    InvoiceExtractionResult,
    InvoiceValidationIssue,
} from 'src/service/InvoiceExtractionService';

const ACCEPTED_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/tiff'];
const MAX_FILE_SIZE = 15 * 1024 * 1024;

const formatFileSize = (size: number): string => `${(size / 1024 / 1024).toFixed(size >= 1024 * 1024 ? 1 : 2)} MB`;

const formatMoney = (amount: number | null, currency: string | null): string => {
    if (amount === null) return '—';
    if (!currency) return amount.toFixed(2);
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency,
        }).format(amount);
    } catch {
        return `${amount.toFixed(2)} ${currency || ''}`.trim();
    }
};

const statusPalette = (status: string): string => {
    if (status === 'parsed') return 'green';
    if (status === 'needs_review') return 'orange';
    return 'red';
};

const issuePalette = (issue: InvoiceValidationIssue): string => issue.severity === 'error' ? 'red' : 'orange';

const ResultField = ({ label, value }: { label: string; value: string | null }) => (
    <Box>
        <Text color='fg.muted' fontSize='xs' fontWeight='semibold' letterSpacing='wide' textTransform='uppercase'>
            {label}
        </Text>
        <Text mt='1' fontWeight='semibold'>{value || '—'}</Text>
    </Box>
);

const ExtractionResult = ({ result }: { result: InvoiceExtractionResult }) => {
    const { t } = useI18n();
    const { invoice, validation, trace } = result;
    const confidence = Math.round(validation.confidence * 100);

    return (
        <Stack gap='5'>
            <Card.Root shadow='sm' borderColor='border.emphasized'>
                <Card.Header>
                    <Flex align='start' justify='space-between' gap='4' wrap='wrap'>
                        <HStack gap='3'>
                            <Flex align='center' justify='center' boxSize='10' rounded='lg' bg='green.50' color='green.600'>
                                <Icon as={LuFileCheck2} boxSize='5' />
                            </Flex>
                            <Box>
                                <Heading size='md'>{t('expenseExtraction.result.title')}</Heading>
                                <Text color='fg.muted' fontSize='sm'>{t('expenseExtraction.result.subtitle')}</Text>
                            </Box>
                        </HStack>
                        <HStack gap='2'>
                            <Badge colorPalette={statusPalette(result.status)} variant='subtle'>
                                {t(`expenseExtraction.status.${result.status}`)}
                            </Badge>
                            <Badge colorPalette={confidence >= 90 ? 'green' : confidence >= 60 ? 'orange' : 'red'} variant='outline'>
                                {t('expenseExtraction.result.confidence', { confidence })}
                            </Badge>
                        </HStack>
                    </Flex>
                </Card.Header>
                <Card.Body>
                    <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap='6'>
                        <ResultField label={t('expenseExtraction.fields.supplier')} value={invoice.supplier_name} />
                        <ResultField label={t('expenseExtraction.fields.invoiceNumber')} value={invoice.invoice_number} />
                        <ResultField label={t('expenseExtraction.fields.invoiceDate')} value={invoice.invoice_date} />
                        <ResultField label={t('expenseExtraction.fields.dueDate')} value={invoice.due_date} />
                        <ResultField label={t('expenseExtraction.fields.taxId')} value={invoice.supplier_tax_id} />
                        <ResultField label={t('expenseExtraction.fields.purchaseOrder')} value={invoice.purchase_order} />
                        <ResultField label={t('expenseExtraction.fields.subtotal')} value={formatMoney(invoice.subtotal, invoice.currency)} />
                        <ResultField label={t('expenseExtraction.fields.tax')} value={formatMoney(invoice.tax, invoice.currency)} />
                    </SimpleGrid>
                    <Flex mt='6' pt='5' borderTopWidth='1px' align='end' justify='space-between' gap='4' wrap='wrap'>
                        <Box>
                            <Text color='fg.muted' fontSize='sm'>{t('expenseExtraction.fields.total')}</Text>
                            <Heading size='2xl' color='blue.700'>{formatMoney(invoice.total, invoice.currency)}</Heading>
                        </Box>
                        <Text color='fg.muted' fontSize='xs'>
                            {t('expenseExtraction.result.processedBy', {
                                provider: result.provider_used || '—',
                                elapsed: trace.elapsed_ms === null ? '—' : `${Math.round(trace.elapsed_ms)} ms`,
                            })}
                        </Text>
                    </Flex>
                </Card.Body>
            </Card.Root>

            {validation.issues.length > 0 && (
                <Card.Root shadow='sm'>
                    <Card.Header>
                        <Heading size='md'>{t('expenseExtraction.validation.title')}</Heading>
                    </Card.Header>
                    <Card.Body pt='0'>
                        <Stack gap='2'>
                            {validation.issues.map((issue) => (
                                <Flex key={`${issue.code}-${issue.message}`} gap='3' align='start' p='3' bg={`${issuePalette(issue)}.50`} rounded='md'>
                                    <Icon as={LuShieldAlert} color={`${issuePalette(issue)}.600`} mt='0.5' />
                                    <Box>
                                        <Text fontWeight='semibold'>{issue.message}</Text>
                                        <Text color='fg.muted' fontSize='xs'>{issue.code}</Text>
                                    </Box>
                                </Flex>
                            ))}
                        </Stack>
                    </Card.Body>
                </Card.Root>
            )}

            {invoice.line_items.length > 0 && (
                <Card.Root shadow='sm' overflow='hidden'>
                    <Card.Header><Heading size='md'>{t('expenseExtraction.lines.title')}</Heading></Card.Header>
                    <Card.Body p='0' overflowX='auto'>
                        <Table.Root size='sm' minW='42rem'>
                            <Table.Header>
                                <Table.Row>
                                    <Table.ColumnHeader>{t('expenseExtraction.lines.description')}</Table.ColumnHeader>
                                    <Table.ColumnHeader>{t('expenseExtraction.lines.sku')}</Table.ColumnHeader>
                                    <Table.ColumnHeader textAlign='end'>{t('expenseExtraction.lines.quantity')}</Table.ColumnHeader>
                                    <Table.ColumnHeader textAlign='end'>{t('expenseExtraction.lines.unitPrice')}</Table.ColumnHeader>
                                    <Table.ColumnHeader textAlign='end'>{t('expenseExtraction.lines.amount')}</Table.ColumnHeader>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {invoice.line_items.map((line, index) => (
                                    <Table.Row key={`${line.sku || line.description || 'line'}-${index}`}>
                                        <Table.Cell>{line.description || '—'}</Table.Cell>
                                        <Table.Cell>{line.sku || '—'}</Table.Cell>
                                        <Table.Cell textAlign='end'>{line.quantity ?? '—'}</Table.Cell>
                                        <Table.Cell textAlign='end'>{formatMoney(line.unit_price, invoice.currency)}</Table.Cell>
                                        <Table.Cell textAlign='end' fontWeight='semibold'>{formatMoney(line.amount, invoice.currency)}</Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table.Root>
                    </Card.Body>
                </Card.Root>
            )}

            <Box as='details' fontSize='sm'>
                <Box as='summary' cursor='pointer' color='fg.muted' fontWeight='semibold'>
                    {t('expenseExtraction.result.raw')}
                </Box>
                <Code as='pre' display='block' mt='3' p='4' maxH='24rem' overflow='auto' whiteSpace='pre-wrap'>
                    {JSON.stringify(result, null, 2)}
                </Code>
            </Box>
        </Stack>
    );
};

const ExpenseExtraction = () => {
    const { t } = useI18n();
    const inputRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [result, setResult] = useState<InvoiceExtractionResult | null>(null);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const selectFile = (nextFile?: File): void => {
        if (!nextFile) return;
        setError('');
        setResult(null);

        if (!ACCEPTED_FILE_TYPES.includes(nextFile.type)) {
            setFile(null);
            setError(t('expenseExtraction.errors.type'));
            return;
        }
        if (nextFile.size > MAX_FILE_SIZE) {
            setFile(null);
            setError(t('expenseExtraction.errors.size'));
            return;
        }
        setFile(nextFile);
    };

    const onFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
        selectFile(event.target.files?.[0]);
    };

    const onDrop = (event: DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
        setIsDragging(false);
        selectFile(event.dataTransfer.files?.[0]);
    };

    const extract = async (): Promise<void> => {
        if (!file) return;
        setIsLoading(true);
        setError('');
        setResult(null);
        try {
            setResult(await invoiceExtractionService.extract(file));
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : t('expenseExtraction.errors.generic'));
        } finally {
            setIsLoading(false);
        }
    };

    const reset = (): void => {
        setFile(null);
        setResult(null);
        setError('');
        if (inputRef.current) inputRef.current.value = '';
    };

    return (
        <Box flex='1' bg='gray.50' py={{ base: '6', md: '10' }}>
            <Container maxW='6xl'>
                <Stack gap='8'>
                    <Box>
                        <HStack gap='3' mb='3'>
                            <Badge colorPalette='purple' variant='subtle' px='2.5' py='1'>
                                <HStack gap='1.5'><Icon as={LuFlaskConical} />{t('expenseExtraction.experimental')}</HStack>
                            </Badge>
                        </HStack>
                        <Heading as='h1' size='3xl'>{t('expenseExtraction.title')}</Heading>
                        <Text mt='3' color='fg.muted' fontSize='lg' maxW='3xl'>{t('expenseExtraction.intro')}</Text>
                    </Box>

                    <Grid templateColumns={{ base: '1fr', lg: result ? 'minmax(18rem, 0.7fr) minmax(0, 1.3fr)' : '1fr' }} gap='6' alignItems='start'>
                        <Card.Root shadow='sm' maxW={result ? undefined : '3xl'} w='full'>
                            <Card.Body>
                                <Stack gap='5'>
                                    <Box
                                        role='button'
                                        tabIndex={0}
                                        aria-label={t('expenseExtraction.upload.action')}
                                        onClick={() => inputRef.current?.click()}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') inputRef.current?.click();
                                        }}
                                        onDragEnter={(event) => { event.preventDefault(); setIsDragging(true); }}
                                        onDragOver={(event) => event.preventDefault()}
                                        onDragLeave={() => setIsDragging(false)}
                                        onDrop={onDrop}
                                        borderWidth='2px'
                                        borderStyle='dashed'
                                        borderColor={isDragging ? 'blue.400' : file ? 'green.300' : 'gray.300'}
                                        bg={isDragging ? 'blue.50' : file ? 'green.50' : 'white'}
                                        rounded='xl'
                                        p={{ base: '7', md: '10' }}
                                        textAlign='center'
                                        cursor='pointer'
                                        transition='all 150ms ease'
                                        _hover={{ borderColor: 'blue.400', bg: 'blue.50' }}
                                    >
                                        <Input
                                            ref={inputRef}
                                            type='file'
                                            accept='.pdf,image/jpeg,image/png,image/webp,image/tiff'
                                            display='none'
                                            onChange={onFileChange}
                                        />
                                        <Flex mx='auto' mb='4' align='center' justify='center' boxSize='14' rounded='full' bg={file ? 'green.100' : 'blue.100'} color={file ? 'green.700' : 'blue.700'}>
                                            <Icon as={file ? LuFileCheck2 : LuFileUp} boxSize='7' />
                                        </Flex>
                                        <Heading size='md'>{file ? file.name : t('expenseExtraction.upload.title')}</Heading>
                                        <Text mt='2' color='fg.muted' fontSize='sm'>
                                            {file ? formatFileSize(file.size) : t('expenseExtraction.upload.hint')}
                                        </Text>
                                        {!file && <Text mt='1' color='fg.subtle' fontSize='xs'>{t('expenseExtraction.upload.formats')}</Text>}
                                    </Box>

                                    {error && <ErrorMessage message={error} />}

                                    <Flex gap={2} justify='flex-end' wrap='wrap'>
                                        {(file || result) && (
                                            <Button variant='outline' colorPalette='gray' onClick={reset} disabled={isLoading}>
                                                <Icon as={LuRefreshCw} />{t('expenseExtraction.actions.reset')}
                                            </Button>
                                        )}
                                        <Button colorPalette='blue' onClick={() => void extract()} disabled={!file} loading={isLoading} loadingText={t('expenseExtraction.actions.processing')}>
                                            {t('expenseExtraction.actions.process')}
                                        </Button>
                                    </Flex>

                                    {isLoading && (
                                        <HStack justify='center' color='fg.muted' py='2'>
                                            <Spinner size='sm' />
                                            <Text fontSize='sm'>{t('expenseExtraction.processingHint')}</Text>
                                        </HStack>
                                    )}
                                </Stack>
                            </Card.Body>
                        </Card.Root>

                        {result && <ExtractionResult result={result} />}
                    </Grid>

                    <Text color='fg.subtle' fontSize='xs' maxW='3xl'>
                        {t('expenseExtraction.privacy')}
                    </Text>
                </Stack>
            </Container>
        </Box>
    );
};

export default ExpenseExtraction;
